import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { API } from "../lib/api";

const SettingsContext = createContext(null);

function hexToHsl(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0; const light = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = light > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(light * 100)}%`;
}

export function logoUrl(path) {
  if (!path) return null;
  return `${API}/files/${path}`;
}

const DEFAULT = { school_name: "School", primary_color: "#4F46E5", background_color: "#F8FAFC", page_style: "modern", logo_path: "", welcome_title: "", welcome_subtitle: "", welcome_image_path: "", welcome_image_position: "left", welcome_text_position: "center", welcome_text_align: "start" };

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT);

  const apply = useCallback((s) => {
    const root = document.documentElement;
    root.style.setProperty("--brand", s.primary_color);
    root.style.setProperty("--app-bg", s.background_color);
    if (s.school_name) document.title = s.school_name;
    try {
      const hsl = hexToHsl(s.primary_color);
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--ring", hsl);
    } catch (err) {
      console.error("Failed to apply primary color", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      apply(data);
    } catch (err) {
      console.error("Failed to load settings", err);
    }
  }, [apply]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (updates) => {
    const { data } = await api.put("/settings", updates);
    setSettings(data);
    apply(data);
    return data;
  }, [apply]);

  const value = useMemo(() => ({ settings, setSettings, refresh, save }), [settings, refresh, save]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
